/**
 * Pure derivations for the assign-time group layout (GROUP-ORIENTATION-PLAN
 * decision 2026-09-18): assigning an orientation to a cohort now takes zero
 * or more groups, everyone not placed in a group becomes an individual
 * assignment, and the default layout is "everyone individually".
 *
 * The module refuses an inconsistent layout WHOLE (`assign_cohort_with_groups`
 * plan_layout rules), so the portal pre-checks the same rules here — a shape
 * the portal can see wrong should never reach the server just to harvest the
 * refusal.
 */

export type GroupModeTag = 'Managed' | 'Auto';

/** One group in the editor. `key` is a local list key, never sent. */
export type LayoutGroup = {
  key: number;
  name: string;
  memberEmails: string[];
  /** '' = the caller leads (the module's default). */
  leaderEmail: string;
  modeTag: GroupModeTag;
  /** Phase 10: off = a member who never started is refused once Running. */
  allowLateJoin: boolean;
  /** Phase 10, Auto only: stall countdown in seconds, 0 = wait forever. */
  autoStallSeconds: number;
};

const norm = (e: string) => e.trim().toLowerCase();

/** The group's effective leader once the module applies "empty = caller". */
export function effectiveLeader(group: LayoutGroup, callerEmail: string): string {
  return group.leaderEmail !== '' ? group.leaderEmail : callerEmail;
}

/** Roster members in no group — they become individual assignments. */
export function ungroupedEmails(
  roster: readonly string[],
  groups: readonly LayoutGroup[],
): string[] {
  const grouped = new Set(groups.flatMap((g) => g.memberEmails.map(norm)));
  return roster.filter((e) => !grouped.has(norm(e)));
}

/** Roster members who LEAD a group: they take no assignment under the
 *  pairing (decision 7 — no credit, no review). */
export function rosterLeaders(
  roster: readonly string[],
  groups: readonly LayoutGroup[],
  callerEmail: string,
): string[] {
  const leaders = new Set(groups.map((g) => norm(effectiveLeader(g, callerEmail))));
  return roster.filter((e) => leaders.has(norm(e)));
}

/**
 * The first blocking problem with the layout, or null when it can be sent.
 * Mirrors the module's refused-whole rules: every group needs a name and at
 * least one member, a member sits in at most one group, and a leader cannot
 * be a member of any group in the layout.
 */
export function layoutProblem(
  groups: readonly LayoutGroup[],
  callerEmail: string,
): string | null {
  const labelOf = (g: LayoutGroup, i: number) =>
    g.name.trim() !== '' ? `"${g.name}"` : `group ${i + 1}`;
  const seen = new Map<string, string>(); // normalized email -> group label
  const leaders = new Map(
    groups.map((g, i) => [norm(effectiveLeader(g, callerEmail)), labelOf(g, i)]),
  );
  for (const [i, g] of groups.entries()) {
    const label = labelOf(g, i);
    if (g.name.trim() === '') return `Name ${label} — every group needs a name.`;
    if (g.memberEmails.length === 0) return `${label} has no members.`;
    for (const email of g.memberEmails) {
      const n = norm(email);
      const already = seen.get(n);
      if (already !== undefined)
        return `${email} is in both ${already} and ${label} — one group per member.`;
      seen.set(n, label);
      const leads = leaders.get(n);
      if (leads !== undefined)
        return `${email} leads ${leads} and is a member of ${label} — a leader cannot be a member.`;
    }
  }
  return null;
}

/** Normalized effective leaders of every group in the layout. */
export function layoutLeaders(
  groups: readonly LayoutGroup[],
  callerEmail: string,
): string[] {
  return [...new Set(groups.map((g) => norm(effectiveLeader(g, callerEmail))))];
}

/**
 * Set a group's leader AND strip that person's member chips from EVERY
 * group in the layout (#170: the leader and the members are disjoint
 * sets — the module refuses a leader who is a member of any group, so
 * the page makes that state unbuildable instead of validating it).
 */
export function withLeader(
  groups: readonly LayoutGroup[],
  key: number,
  leaderEmail: string,
  callerEmail: string,
): LayoutGroup[] {
  const n = norm(leaderEmail !== '' ? leaderEmail : callerEmail);
  return groups.map((g) => ({
    ...g,
    leaderEmail: g.key === key ? leaderEmail : g.leaderEmail,
    memberEmails: g.memberEmails.filter((e) => norm(e) !== n),
  }));
}

/** The review card's one-line description of what the layout creates. */
export function layoutSummary(
  roster: readonly string[],
  groups: readonly LayoutGroup[],
  callerEmail: string,
): string {
  if (groups.length === 0) return `everyone individually (${roster.length})`;
  const grouped = groups.reduce((acc, g) => acc + g.memberEmails.length, 0);
  const leads = rosterLeaders(roster, groups, callerEmail);
  const individuals = ungroupedEmails(roster, groups).filter(
    (e) => !leads.some((l) => norm(l) === norm(e)),
  );
  const parts = [
    `${groups.length} group${groups.length === 1 ? '' : 's'} (${grouped} member${grouped === 1 ? '' : 's'})`,
    `${individuals.length} individually`,
  ];
  if (leads.length > 0)
    parts.push(
      `${leads.length} lead${leads.length === 1 ? 's' : ''} without taking the orientation`,
    );
  return parts.join(' · ');
}
